import React, { useState, useEffect, useContext } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Modal,
  Form,
  Alert,
  Spinner,
  Badge,
} from "react-bootstrap";
import {
  IconSettings,
  IconKey,
  IconPlus,
  IconEdit,
  IconTrash,
  IconCheck,
  IconX,
  IconAlertTriangle,
  IconBell,
  IconClock,
} from "@tabler/icons-react";
import Swal from "sweetalert2";

import UserContext from "../../contexts/UserContext";
import NotificationsContext from "../../contexts/NotificationsContext";
import { getAccountSettings, updateAccountSettings } from "../../services/SettingsServices";
import NotificationService from "../../services/NotificationsServices";
import Select from "react-select";
import NumberFormat from "react-number-format";

const notificationTypes = [
  { value: "whatsapp", label: "Mensagem no WhatsApp" },
  { value: "platform", label: "Mensagem na Plataforma" },
];

const GlobalSettings = () => {
  const { user } = useContext(UserContext);
  const { setNotify } = useContext(NotificationsContext);

  const [loading, setLoading] = useState(false);
  const [settingsData, setSettingsData] = useState(null);
  const [notificationsData, setNotificationsData] = useState(null);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [apiKeyForm, setApiKeyForm] = useState({
    api_key: "",
    isEditing: false,
  });
  const [modalLoading, setModalLoading] = useState(false);

  // Estados para notificações
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notificationForm, setNotificationForm] = useState({
    keyword: "",
    notificationTypes: [],
    phones: [""], // Array de telefones (máx 3)
    isEditing: false,
  });
  const [showPhoneField, setShowPhoneField] = useState(false);
  const MAX_PHONES = 3;

  // Estados para reativação da IA
  const [showAiReactivationModal, setShowAiReactivationModal] = useState(false);
  const [aiReactivationForm, setAiReactivationForm] = useState({
    ai_auto_reactivation_enabled: false,
    ai_auto_reactivation_minutes: 5,
    isEditing: false,
  });

  // Carregar configurações ao montar o componente
  useEffect(() => {
    loadSettings();
    loadNotifications();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await getAccountSettings();
      setSettingsData(response.data || {});
    } catch (error) {
      console.error("Erro ao carregar configurações:", error);
      setNotify({
        type: "error",
        title: "Erro",
        message: "Erro ao carregar configurações da conta",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadNotifications = async () => {
    try {
      const config = await NotificationService.getCurrentConfig();
      if (config?.keyword) setNotificationsData(config);
    } catch (error) {
      console.error("Erro ao carregar configurações de notificação:", error);
      // Não mostra erro se não houver configuração ainda
      setNotificationsData(null);
    }
  };

  const handleApiKeyModal = (isEditing = false) => {
    setApiKeyForm({
      api_key: isEditing && settingsData?.api_key ? settingsData.api_key : "",
      isEditing,
    });
    setShowApiKeyModal(true);
  };

  const closeApiKeyModal = () => {
    setShowApiKeyModal(false);
    setApiKeyForm({ api_key: "", isEditing: false });
  };

  const handleApiKeySubmit = async (e) => {
    e.preventDefault();

    if (!apiKeyForm.api_key.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Campo obrigatório",
        text: "Por favor, insira a API Key",
      });
      return;
    }

    try {
      setModalLoading(true);

      const response = await updateAccountSettings({
        api_key: apiKeyForm.api_key.trim(),
      });

      if (response.status === 200) {
        setNotify({
          type: "success",
          title: "Sucesso",
          message: apiKeyForm.isEditing 
            ? "API Key atualizada com sucesso!" 
            : "API Key cadastrada com sucesso!",
        });

        // Atualizar dados locais
        setSettingsData({
          ...settingsData,
          api_key: apiKeyForm.api_key.trim(),
        });
        localStorage.removeItem("__accsettings");
        closeApiKeyModal();

        Swal.fire({
          icon: "success",
          title: "API Key Configurada",
          text: apiKeyForm.isEditing 
            ? "Sua API Key foi atualizada com sucesso!" 
            : "Sua API Key global foi configurada com sucesso!",
          timer: 2000,
        }).then((value) => {
          if(value.isConfirmed){
            window.location.reload();
          }
        });

        
      }
    } catch (error) {
      console.error("Erro ao salvar API Key:", error);
      
      let errorMessage = "Erro ao salvar API Key. Tente novamente.";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      Swal.fire({
        icon: "error",
        title: "Erro ao salvar",
        text: errorMessage,
      });

      setNotify({
        type: "error",
        title: "Erro",
        message: errorMessage,
      });
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteApiKey = async () => {
    const result = await Swal.fire({
      title: "Remover API Key",
      text: "Tem certeza que deseja remover a API Key global? Esta ação não pode ser desfeita.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sim, remover",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        
        const response = await updateAccountSettings({
          api_key: null,
        });

        if (response.status === 200) {
          setSettingsData({
            ...settingsData,
            api_key: null,
          });

          setNotify({
            type: "success", 
            title: "Sucesso",
            message: "API Key removida com sucesso!",
          });

          Swal.fire({
            icon: "success",
            title: "API Key Removida",
            text: "A API Key global foi removida com sucesso!",
            timer: 2000,
          });
        }
      } catch (error) {
        console.error("Erro ao remover API Key:", error);
        
        setNotify({
          type: "error",
          title: "Erro",
          message: "Erro ao remover API Key. Tente novamente.",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const maskApiKey = (apiKey) => {
    if (!apiKey || apiKey.length < 8) return apiKey;
    return apiKey.substring(0, 4) + "****" + apiKey.substring(apiKey.length - 4);
  };

  // Funções para Notificações
  const handleNotificationModal = (isEditing = false) => {
    if (isEditing && notificationsData) {
      // Converter phone para phones array se necessário (backward compatibility)
      let phonesArray = [""];
      if (notificationsData.phones && Array.isArray(notificationsData.phones)) {
        phonesArray = notificationsData.phones.length > 0 ? notificationsData.phones : [""];
      } else if (notificationsData.phone) {
        phonesArray = [notificationsData.phone];
      }

      setNotificationForm({
        keyword: notificationsData.keyword || "",
        notificationTypes: notificationsData.notificationTypes || [],
        phones: phonesArray,
        isEditing,
      });
      setShowPhoneField(
        notificationsData.notificationTypes?.some(
          (type) => type.value === "whatsapp"
        ) || false
      );
    } else {
      setNotificationForm({
        keyword: "",
        notificationTypes: [],
        phones: [""],
        isEditing: false,
      });
      setShowPhoneField(false);
    }
    setShowNotificationModal(true);
  };

  const closeNotificationModal = () => {
    setShowNotificationModal(false);
    setNotificationForm({
      keyword: "",
      notificationTypes: [],
      phones: [""],
      isEditing: false,
    });
    setShowPhoneField(false);
  };

  // Funções para gerenciar múltiplos telefones
  const handleAddPhone = () => {
    if (notificationForm.phones.length < MAX_PHONES) {
      setNotificationForm({
        ...notificationForm,
        phones: [...notificationForm.phones, ""],
      });
    }
  };

  const handleRemovePhone = (index) => {
    if (notificationForm.phones.length > 1) {
      const newPhones = notificationForm.phones.filter((_, i) => i !== index);
      setNotificationForm({
        ...notificationForm,
        phones: newPhones,
      });
    }
  };

  const handlePhoneChange = (index, value) => {
    const newPhones = [...notificationForm.phones];
    newPhones[index] = value;
    setNotificationForm({
      ...notificationForm,
      phones: newPhones,
    });
  };

  const handleNotificationSubmit = async (e) => {
    e.preventDefault();

    if (!notificationForm.notificationTypes.length) {
      Swal.fire({
        icon: "warning",
        title: "Campo obrigatório",
        text: "Selecione pelo menos um tipo de notificação",
      });
      return;
    }

    const hasWhatsApp = notificationForm.notificationTypes.some(
      (type) => type.value === "whatsapp"
    );

    // Filtrar telefones válidos (não vazios)
    const validPhones = notificationForm.phones.filter(phone => phone.trim() !== "");

    if (hasWhatsApp && validPhones.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "Campo obrigatório",
        text: "Pelo menos um telefone é obrigatório quando WhatsApp está selecionado",
      });
      return;
    }

    try {
      setModalLoading(true);

      const configData = {
        keyword: "humano, humana",
        notificationTypes: notificationForm.notificationTypes,
        phones: validPhones, // Enviar sempre como array
      };

      if (notificationForm.isEditing && notificationsData?.id) {
        await NotificationService.updateConfig(notificationsData.id, configData);
      } else {
        await NotificationService.saveConfig(configData);
      }

      setNotify({
        type: "success",
        title: "Sucesso",
        message: notificationForm.isEditing
          ? "Notificações atualizadas com sucesso!"
          : "Notificações configuradas com sucesso!",
      });

      // Recarregar configurações de notificação
      await loadNotifications();
      
      // Atualizar localStorage para que o NotificationsContext use a nova palavra-chave
      localStorage.removeItem("__accsettings");

      closeNotificationModal();

      Swal.fire({
        icon: "success",
        title: "Notificações Configuradas",
        text: notificationForm.isEditing
          ? "Suas notificações foram atualizadas com sucesso!"
          : "Suas notificações foram configuradas com sucesso!",
        timer: 2000,
      }).then((value) => {
        if (value.isConfirmed) {
          window.location.reload();
        }
      });;
    } catch (error) {
      console.error("Erro ao salvar notificações:", error);

      let errorMessage = "Erro ao salvar notificações. Tente novamente.";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      Swal.fire({
        icon: "error",
        title: "Erro ao salvar",
        text: errorMessage,
      });

      setNotify({
        type: "error",
        title: "Erro",
        message: errorMessage,
      });
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteNotifications = async () => {
    const result = await Swal.fire({
      title: "Remover Notificações",
      text: "Tem certeza que deseja remover as configurações de notificação? Esta ação não pode ser desfeita.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sim, remover",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);

        if (notificationsData?.id) {
          await NotificationService.deleteConfig(notificationsData.id);
        }

        setNotificationsData(null);
        
        // Limpar localStorage para que o NotificationsContext pare de usar a palavra-chave
        localStorage.removeItem("__accsettings");

        setNotify({
          type: "success",
          title: "Sucesso",
          message: "Notificações removidas com sucesso!",
        });

        Swal.fire({
          icon: "success",
          title: "Notificações Removidas",
          text: "As configurações de notificação foram removidas com sucesso!",
          timer: 2000,
        });
      } catch (error) {
        console.error("Erro ao remover notificações:", error);

        setNotify({
          type: "error",
          title: "Erro",
          message: "Erro ao remover notificações. Tente novamente.",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  // Funções para Reativação da IA
  const handleAiReactivationModal = (isEditing = false) => {
    if (isEditing && settingsData) {
      setAiReactivationForm({
        ai_auto_reactivation_enabled: settingsData.ai_auto_reactivation_enabled || false,
        ai_auto_reactivation_minutes: settingsData.ai_auto_reactivation_minutes || 5,
        isEditing,
      });
    } else {
      setAiReactivationForm({
        ai_auto_reactivation_enabled: false,
        ai_auto_reactivation_minutes: 5,
        isEditing: false,
      });
    }
    setShowAiReactivationModal(true);
  };

  const closeAiReactivationModal = () => {
    setShowAiReactivationModal(false);
    setAiReactivationForm({
      ai_auto_reactivation_enabled: false,
      ai_auto_reactivation_minutes: 5,
      isEditing: false,
    });
  };

  const handleAiReactivationSubmit = async (e) => {
    e.preventDefault();

    if (aiReactivationForm.ai_auto_reactivation_enabled && aiReactivationForm.ai_auto_reactivation_minutes < 5) {
      Swal.fire({
        icon: "warning",
        title: "Valor inválido",
        text: "O tempo mínimo de reativação é 5 minutos",
      });
      return;
    }

    try {
      setModalLoading(true);

      const response = await updateAccountSettings({
        ai_auto_reactivation_enabled: aiReactivationForm.ai_auto_reactivation_enabled,
        ai_auto_reactivation_minutes: aiReactivationForm.ai_auto_reactivation_minutes,
      });

      if (response.status === 200) {
        setNotify({
          type: "success",
          title: "Sucesso",
          message: aiReactivationForm.isEditing 
            ? "Configurações de reativação da IA atualizadas com sucesso!" 
            : "Configurações de reativação da IA salvas com sucesso!",
        });

        // Atualizar dados locais
        setSettingsData({
          ...settingsData,
          ai_auto_reactivation_enabled: aiReactivationForm.ai_auto_reactivation_enabled,
          ai_auto_reactivation_minutes: aiReactivationForm.ai_auto_reactivation_minutes,
        });
        
        closeAiReactivationModal();

        Swal.fire({
          icon: "success",
          title: "Configurações Salvas",
          text: aiReactivationForm.isEditing 
            ? "Suas configurações de reativação da IA foram atualizadas com sucesso!" 
            : "Suas configurações de reativação da IA foram salvas com sucesso!",
          timer: 2000,
        });
      }
    } catch (error) {
      console.error("Erro ao salvar configurações de reativação:", error);
      
      let errorMessage = "Erro ao salvar configurações. Tente novamente.";
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      Swal.fire({
        icon: "error",
        title: "Erro ao salvar",
        text: errorMessage,
      });

      setNotify({
        type: "error",
        title: "Erro",
        message: errorMessage,
      });
    } finally {
      setModalLoading(false);
    }
  };

  if (loading && !settingsData) {
    return (
      <Container className="mt-4">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Carregando...</span>
          </Spinner>
          <p className="mt-2 text-muted">Carregando configurações...</p>
        </div>
      </Container>
    );
  }

  return (
    <>
      {/* Mobile-first Container */}
      <div className="px-3 px-sm-4 py-3 py-sm-4">
        <Container fluid className="px-0">
          <Row className="g-0">
            <Col xs={12} lg={12} xl={12} className="mx-auto">
              {/* Mobile Header */}
              <div className="mb-3 mb-md-4">
                <div className="d-flex align-items-start flex-column flex-sm-row">
                  <div className="d-flex align-items-center mb-2 mb-sm-0">
                    <IconSettings
                      className="me-2 me-sm-3 text-primary"
                      size={28}
                    />
                    <div>
                      <h1 className="h4 h-md-2 mb-1">Configurações Globais</h1>
                    </div>
                  </div>
                </div>
                <p className="text-muted mb-0 mt-2 mt-sm-0 small">
                  Gerencie as configurações gerais da sua conta
                </p>
              </div>

              {/* Cards Side by Side - Responsive */}
              <Row className="g-3 g-md-4">
                {/* API Key Section */}
                <Col xs={12} lg={4}>
                  <Card className="shadow-sm border-0 h-100">
                    <Card.Header className="bg-light border-0 px-3 px-sm-4 py-3">
                      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                        <div className="d-flex align-items-center flex-wrap">
                          <IconKey className="me-2 text-primary" size={18} />
                          <h5 className="mb-0 h6 h-sm-5">API Key Global</h5>
                        </div>
                        <Badge
                          bg={settingsData?.api_key ? "success" : "secondary"}
                          className="px-2 py-1"
                        >
                          <small>
                            {settingsData?.api_key
                              ? "Configurada"
                              : "Não configurada"}
                          </small>
                        </Badge>
                      </div>
                    </Card.Header>
                    <Card.Body className="px-3 px-sm-4 py-3 py-sm-4 d-flex flex-column">
                      <p className="text-muted mb-3 small">
                        Configure uma API Key global que será usada como padrão
                        em toda utilização da conta quando desejar gerar
                        conteúdo com IA.
                      </p>

                      <div className="flex-grow-1">
                        {settingsData?.api_key ? (
                          <div className="bg-light rounded p-3 mb-3">
                            <div className="mb-3">
                              <small className="text-muted d-block mb-1">
                                API Key configurada:
                              </small>
                              <code className="text-dark d-block text-break small">
                                {maskApiKey(settingsData.api_key)}
                              </code>
                            </div>

                            {/* Mobile-first Button Layout */}
                            <div className="d-grid d-sm-flex gap-2 justify-content-sm-end">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleApiKeyModal(true)}
                                disabled={loading}
                                className="d-flex align-items-center justify-content-center"
                              >
                                <IconEdit size={16} className="me-1" />
                                <span>Editar</span>
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={handleDeleteApiKey}
                                disabled={loading}
                                className="d-flex align-items-center justify-content-center"
                              >
                                <IconTrash size={16} className="me-1" />
                                <span>Remover</span>
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-4 py-sm-5">
                            <IconAlertTriangle
                              className="text-warning mb-3"
                              size={40}
                            />
                            <h6 className="text-muted mb-3 h6">
                              Nenhuma API Key configurada
                            </h6>
                            <div className="d-grid d-sm-flex justify-content-sm-center">
                              <Button
                                variant="primary"
                                onClick={() => handleApiKeyModal(false)}
                                disabled={loading}
                                className="d-flex align-items-center justify-content-center"
                              >
                                <IconPlus size={16} className="me-1" />
                                <span>Configurar API Key</span>
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Notifications Section */}
                <Col xs={12} lg={4}>
                  <Card className="shadow-sm border-0 h-100">
                    <Card.Header className="bg-light border-0 px-3 px-sm-4 py-3">
                      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                        <div className="d-flex align-items-center flex-wrap">
                          <IconBell className="me-2 text-primary" size={18} />
                          <h5 className="mb-0 h6 h-sm-5">Atendimento humano</h5>
                        </div>
                        <Badge
                          bg={notificationsData ? "success" : "secondary"}
                          className="px-2 py-1"
                        >
                          <small>
                            {notificationsData
                              ? "Configuradas"
                              : "Não configurada"}
                          </small>
                        </Badge>
                      </div>
                    </Card.Header>
                    <Card.Body className="px-3 px-sm-4 py-3 py-sm-4 d-flex flex-column">
                      <p className="text-muted mb-3 small">
                        Configure onde deseja receber a notificação para o atendimento humano.
                      </p>
                      <div className="flex-grow-1">
                        {notificationsData ? (
                          <div className="bg-light rounded p-3 mb-3">
                            

                            <div className="mb-3">
                              <small className="text-muted d-block mb-1">
                                Tipos de notificação:
                              </small>
                              <div className="d-flex flex-wrap gap-1">
                                {notificationsData.notificationTypes?.map(
                                  (type, index) => (
                                    <Badge
                                      key={index}
                                      bg="info"
                                      className="px-2 py-1"
                                    >
                                      <small>{type.label}</small>
                                    </Badge>
                                  )
                                )}
                              </div>
                            </div>

                            {(notificationsData.phones?.length > 0 || notificationsData.phone) && (
                              <div className="mb-3">
                                <small className="text-muted d-block mb-1">
                                  Telefone(s) WhatsApp:
                                </small>
                                {notificationsData.phones?.length > 0 ? (
                                  notificationsData.phones.map((phone, index) => (
                                    <code key={index} className="text-dark d-block text-break small mb-1">
                                      {index + 1}. {phone}
                                    </code>
                                  ))
                                ) : (
                                  <code className="text-dark d-block text-break small">
                                    {notificationsData.phone}
                                  </code>
                                )}
                              </div>
                            )}

                            {/* Mobile-first Button Layout */}
                            <div className="d-grid d-sm-flex gap-2 justify-content-sm-end">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleNotificationModal(true)}
                                disabled={loading}
                                className="d-flex align-items-center justify-content-center"
                              >
                                <IconEdit size={16} className="me-1" />
                                <span>Editar</span>
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={handleDeleteNotifications}
                                disabled={loading}
                                className="d-flex align-items-center justify-content-center"
                              >
                                <IconTrash size={16} className="me-1" />
                                <span>Remover</span>
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-4 py-sm-5">
                            <IconAlertTriangle
                              className="text-warning mb-3"
                              size={40}
                            />
                            <h6 className="text-muted mb-3 h6">
                              Não configurado!
                            </h6>
                            <div className="d-grid d-sm-flex justify-content-sm-center">
                              <Button
                                variant="primary"
                                onClick={() => handleNotificationModal(false)}
                                disabled={loading}
                                className="d-flex align-items-center justify-content-center"
                              >
                                <IconPlus size={16} className="me-1" />
                                <span>Configurar palavra-chave</span>
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                {/* AI Auto Reactivation Section */}
                <Col xs={12} lg={4}>
                  <Card className="shadow-sm border-0 h-100">
                    <Card.Header className="bg-light border-0 px-3 px-sm-4 py-3">
                      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                        <div className="d-flex align-items-center flex-wrap">
                          <IconClock className="me-2 text-primary" size={18} />
                          <h5 className="mb-0 h6 h-sm-5">Ativação automática da IA</h5>
                        </div>
                        <Badge
                          bg={settingsData?.ai_auto_reactivation_enabled ? "success" : "secondary"}
                          className="px-2 py-1"
                        >
                          <small>
                            {settingsData?.ai_auto_reactivation_enabled
                              ? "Ativada"
                              : "Desativada"}
                          </small>
                        </Badge>
                      </div>
                    </Card.Header>
                    <Card.Body className="px-3 px-sm-4 py-3 py-sm-4 d-flex flex-column">
                      <p className="text-muted mb-3 small">
                        Configure o tempo para reativar automaticamente a IA após o 
                        atendimento ser assumido por um agente humano.
                      </p>
                      <div className="flex-grow-1">
                        {settingsData?.ai_auto_reactivation_enabled ? (
                          <div className="bg-light rounded p-3 mb-3">
                            <div className="mb-3">
                              <small className="text-muted d-block mb-1">
                                Status:
                              </small>
                              <code className="text-success d-block text-break small">
                                Ativada
                              </code>
                            </div>

                            <div className="mb-3">
                              <small className="text-muted d-block mb-1">
                                Tempo para reativação:
                              </small>
                              <code className="text-dark d-block text-break small">
                                {settingsData.ai_auto_reactivation_minutes || 5} minutos
                              </code>
                            </div>

                            {/* Mobile-first Button Layout */}
                            <div className="d-grid d-sm-flex gap-2 justify-content-sm-end">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleAiReactivationModal(true)}
                                disabled={loading}
                                className="d-flex align-items-center justify-content-center"
                              >
                                <IconEdit size={16} className="me-1" />
                                <span>Editar</span>
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-4 py-sm-5">
                            <IconAlertTriangle
                              className="text-warning mb-3"
                              size={40}
                            />
                            <h6 className="text-muted mb-3 h6">
                              Reativação automática desabilitada
                            </h6>
                            <div className="d-grid d-sm-flex justify-content-sm-center">
                              <Button
                                variant="primary"
                                onClick={() => handleAiReactivationModal(false)}
                                disabled={loading}
                                className="d-flex align-items-center justify-content-center"
                              >
                                <IconPlus size={16} className="me-1" />
                                <span>Configurar reativação</span>
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Mobile-optimized Modal para configurar API Key */}
      <Modal
        show={showApiKeyModal}
        onHide={closeApiKeyModal}
        centered
        backdrop="static"
        size="lg"
        className="modal-mobile-fullscreen"
      >
        <Modal.Header closeButton className="px-3 px-sm-4 py-3 border-0">
          <Modal.Title className="h5 h-sm-4 w-100">
            <div className="d-flex align-items-center flex-wrap">
              <IconKey className="me-2 flex-shrink-0" size={18} />
              <span className="text-break">
                {apiKeyForm.isEditing
                  ? "Editar API Key"
                  : "Configurar API Key Global"}
              </span>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleApiKeySubmit}>
          <Modal.Body className="px-3 px-sm-4 py-0 py-sm-3">
            <Alert variant="warning" className="mb-3 border-0">
              <div className="d-flex align-items-start">
                <IconAlertTriangle
                  className="me-2 mt-1 flex-shrink-0"
                  size={16}
                />
                <div className="small">
                  <strong>Atenção:</strong> A API Key será usada internamente
                  para a conta, para gerar conteúdos com IA.
                </div>
              </div>
            </Alert>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">
                API Key <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="password"
                placeholder="Digite sua API Key..."
                value={apiKeyForm.api_key}
                onChange={(e) =>
                  setApiKeyForm({ ...apiKeyForm, api_key: e.target.value })
                }
                required
                disabled={modalLoading}
                className="py-2"
                size="lg"
              />
              <Form.Text className="text-muted small mt-1">
                Esta chave será criptografada e armazenada com segurança.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="px-3 px-sm-4 py-3 border-0 bg-light">
            <div className="d-flex flex-column flex-sm-row gap-2 w-100">
              <Button
                variant="secondary"
                onClick={closeApiKeyModal}
                disabled={modalLoading}
                className="d-flex align-items-center justify-content-center order-2 order-sm-1"
                size="lg"
              >
                <IconX className="me-1" size={16} />
                <span>Cancelar</span>
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={modalLoading || !apiKeyForm.api_key.trim()}
                className="d-flex align-items-center justify-content-center order-1 order-sm-2 flex-sm-grow-1"
                size="lg"
              >
                {modalLoading ? (
                  <Spinner size="sm" className="me-1" />
                ) : (
                  <IconCheck className="me-1" size={16} />
                )}
                <span>{apiKeyForm.isEditing ? "Atualizar" : "Salvar"}</span>
              </Button>
            </div>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Mobile-optimized Modal para configurar Notificações */}
      <Modal
        show={showNotificationModal}
        onHide={closeNotificationModal}
        centered
        backdrop="static"
        size="lg"
        className="modal-mobile-fullscreen"
      >
        <Modal.Header closeButton className="px-3 px-sm-4 py-3 border-0">
          <Modal.Title className="h5 h-sm-4 w-100">
            <div className="d-flex align-items-center flex-wrap">
              <IconBell className="me-2 flex-shrink-0" size={18} />
              <span className="text-break">
                {notificationForm.isEditing
                  ? "Editar Notificações"
                  : "Configurar Notificações"}
              </span>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleNotificationSubmit}>
          <Modal.Body className="px-3 px-sm-4 py-0 py-sm-3">
            <Alert variant="info" className="mb-3 border-0">
              <div className="d-flex align-items-start">
                <IconBell className="me-2 mt-1 flex-shrink-0" size={16} />
                <div className="small">
                  <strong>Info:</strong> O sistema enviará notificações quando o
                  o agente chamar pelo atendimento humano.
                </div>
              </div>
            </Alert>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">
                Tipos de notificação <span className="text-danger">*</span>
              </Form.Label>
              <Select
                isMulti
                options={notificationTypes}
                classNamePrefix="select"
                placeholder="Selecione os tipos de notificação"
                value={notificationForm.notificationTypes}
                onChange={(selectedOptions) => {
                  setNotificationForm({
                    ...notificationForm,
                    notificationTypes: selectedOptions || [],
                  });
                  setShowPhoneField(
                    selectedOptions?.some(
                      (type) => type.value === "whatsapp"
                    ) || false
                  );
                }}
                isDisabled={modalLoading}
              />
            </Form.Group>

            {showPhoneField && (
              <Form.Group className="mb-3">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <Form.Label className="fw-semibold mb-0">
                    Telefone(s) para notificações no WhatsApp{" "}
                    <span className="text-danger">*</span>
                  </Form.Label>
                  <Badge bg="secondary" className="px-2 py-1">
                    <small>{notificationForm.phones.length}/{MAX_PHONES}</small>
                  </Badge>
                </div>

                {notificationForm.phones.map((phone, index) => (
                  <div key={index} className="d-flex align-items-center gap-2 mb-2">
                    <div className="flex-grow-1">
                      <NumberFormat
                        format="(##) #####-####"
                        className="form-control py-2"
                        placeholder={`Telefone ${index + 1}`}
                        onValueChange={(values) => handlePhoneChange(index, values.value)}
                        value={phone}
                        disabled={modalLoading}
                      />
                    </div>
                    {notificationForm.phones.length > 1 && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleRemovePhone(index)}
                        disabled={modalLoading}
                        className="d-flex align-items-center justify-content-center"
                        style={{ width: "38px", height: "38px" }}
                      >
                        <IconTrash size={16} />
                      </Button>
                    )}
                  </div>
                ))}

                {notificationForm.phones.length < MAX_PHONES && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleAddPhone}
                    disabled={modalLoading}
                    className="d-flex align-items-center mt-2"
                  >
                    <IconPlus size={16} className="me-1" />
                    <span>Adicionar telefone</span>
                  </Button>
                )}

                <Form.Text className="text-muted small mt-2 d-block">
                  Você pode adicionar até {MAX_PHONES} números de telefone para receber notificações.
                </Form.Text>
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer className="px-3 px-sm-4 py-3 border-0 bg-light">
            <div className="d-flex flex-column flex-sm-row gap-2 w-100">
              <Button
                variant="secondary"
                onClick={closeNotificationModal}
                disabled={modalLoading}
                className="d-flex align-items-center justify-content-center order-2 order-sm-1"
                size="lg"
              >
                <IconX className="me-1" size={16} />
                <span>Cancelar</span>
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={
                  modalLoading ||
                  !notificationForm.notificationTypes.length
                }
                className="d-flex align-items-center justify-content-center order-1 order-sm-2 flex-sm-grow-1"
                size="lg"
              >
                {modalLoading ? (
                  <Spinner size="sm" className="me-1" />
                ) : (
                  <IconCheck className="me-1" size={16} />
                )}
                <span>
                  {notificationForm.isEditing ? "Atualizar" : "Salvar"}
                </span>
              </Button>
            </div>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Mobile-optimized Modal para configurar Reativação da IA */}
      <Modal
        show={showAiReactivationModal}
        onHide={closeAiReactivationModal}
        centered
        backdrop="static"
        size="lg"
        className="modal-mobile-fullscreen"
      >
        <Modal.Header closeButton className="px-3 px-sm-4 py-3 border-0">
          <Modal.Title className="h5 h-sm-4 w-100">
            <div className="d-flex align-items-center flex-wrap">
              <IconClock className="me-2 flex-shrink-0" size={18} />
              <span className="text-break">
                {aiReactivationForm.isEditing
                  ? "Editar Ativação Automática da IA"
                  : "Configurar Ativação Automática da IA"}
              </span>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAiReactivationSubmit}>
          <Modal.Body className="px-3 px-sm-4 py-0 py-sm-3">
            <Alert variant="info" className="mb-3 border-0">
              <div className="d-flex align-items-start">
                <IconClock className="me-2 mt-1 flex-shrink-0" size={16} />
                <div className="small">
                  <strong>Info:</strong> Quando um agente humano assumir o atendimento, 
                  a IA será reativada automaticamente após o tempo configurado.
                </div>
              </div>
            </Alert>

            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="ai-auto-reactivation-enabled"
                label="Habilitar ativação automática da IA"
                checked={aiReactivationForm.ai_auto_reactivation_enabled}
                onChange={(e) =>
                  setAiReactivationForm({
                    ...aiReactivationForm,
                    ai_auto_reactivation_enabled: e.target.checked,
                  })
                }
                disabled={modalLoading}
                className="py-2"
              />
              <Form.Text className="text-muted small mt-1">
                A IA será reativada automaticamente após o tempo especificado.
              </Form.Text>
            </Form.Group>

            {aiReactivationForm.ai_auto_reactivation_enabled && (
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">
                  Tempo para reativação (minutos) <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  placeholder="5"
                  min="5"
                  max="1440"
                  value={aiReactivationForm.ai_auto_reactivation_minutes}
                  onChange={(e) =>
                    setAiReactivationForm({
                      ...aiReactivationForm,
                      ai_auto_reactivation_minutes: parseInt(e.target.value) || 5,
                    })
                  }
                  required
                  disabled={modalLoading}
                  className="py-2"
                  size="lg"
                />
                <Form.Text className="text-muted small mt-1">
                  Tempo em minutos após assumir o atendimento para reativar a IA (mínimo: 5 minutos).
                </Form.Text>
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer className="px-3 px-sm-4 py-3 border-0 bg-light">
            <div className="d-flex flex-column flex-sm-row gap-2 w-100">
              <Button
                variant="secondary"
                onClick={closeAiReactivationModal}
                disabled={modalLoading}
                className="d-flex align-items-center justify-content-center order-2 order-sm-1"
                size="lg"
              >
                <IconX className="me-1" size={16} />
                <span>Cancelar</span>
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={modalLoading}
                className="d-flex align-items-center justify-content-center order-1 order-sm-2 flex-sm-grow-1"
                size="lg"
              >
                {modalLoading ? (
                  <Spinner size="sm" className="me-1" />
                ) : (
                  <IconCheck className="me-1" size={16} />
                )}
                <span>
                  {aiReactivationForm.isEditing ? "Atualizar" : "Salvar"}
                </span>
              </Button>
            </div>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default GlobalSettings;